import React, { Dispatch, SetStateAction } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { DatePicker } from '../../../../../generic/ui/DatePicker';
import UserIcon from '../../../../../generic/icons/IconLib/UserIcon';
import SettingMiksher from '../../../../../generic/icons/IconLib/SlidersIcon';

interface Props {
    clientName: string;
    setDate: Dispatch<SetStateAction<string | null>>;
    onSettings: () => void;
    showSettings: boolean;
}

const PreTeudaSelector: React.FC<Props> = ({
    clientName,
    setDate,
    onSettings,
    showSettings,
}) => {
    const [t] = useTranslation('teudot');
    const placeholder = clientName?.trim() ?? '';
    const isPlaceholder = !clientName?.trim();

    const handleDateChange: Dispatch<SetStateAction<string>> = (next) => {
        if (typeof next === 'function') return;
        setDate(next);
    };

    return (
        <Card>
            {showSettings && (
                <SettingsBtn type='button' onClick={onSettings} aria-label='settings'>
                    <SettingMiksher />
                </SettingsBtn>
            )}
            <Main>
                <Row $compact>
                    <DatePicker setDate={handleDateChange} width='12rem' mobileWidth='10rem' />
                </Row>
                <Row>
                    {
                        placeholder && (
                            <ClientLine>
                                <IconSlot>
                                    <UserIcon color='currentColor' size={16} />
                                </IconSlot>
                                <ClientName $placeholder={isPlaceholder}>{placeholder}</ClientName>
                            </ClientLine>
                        )
                    }
                </Row>
            </Main>
        </Card>
    );
};

export default PreTeudaSelector;

const Card = styled.div`
    width: 100%;
    border: 1px solid ${({ theme }) => theme.colors.line};
    border-radius: 1rem;
    background: ${({ theme }) => theme.colors.surface};
    padding: 0.75rem 1rem;
    display: flex;
    flex-direction: row-reverse;
    align-items: center;
    gap: 0.75rem;
    direction: rtl;

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        padding: 0.625rem 0.75rem;
    }
`;

const Main = styled.div`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 0.625rem;
`;

const Row = styled.div<{ $compact?: boolean }>`
    display: flex;
    align-items: center;
    gap: 0.5rem;
    direction: rtl;
    ${({ $compact }) =>
        $compact
            ? `
                justify-content: flex-start;
            `
            : `
                justify-content: flex-start;
            `}
`;

const ClientLine = styled.div`
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    min-width: 0;
    max-width: 100%;
`;

const IconSlot = styled.span`
    display: inline-flex;
    align-items: center;
    color: ${({ theme }) => theme.colors.ink3};
    flex-shrink: 0;
`;

const ClientName = styled.span<{ $placeholder?: boolean }>`
    font-family: ${({ theme }) => theme.fonts.sans};
    font-size: ${({ theme }) => theme.text.lg};
    font-weight: 500;
    color: ${({ theme, $placeholder }) =>
        $placeholder ? theme.colors.ink4 : theme.colors.ink};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        font-size: ${({ theme }) => theme.textMobile.lg};
    }
`;

const SettingsBtn = styled.button`
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.875rem;
    height: 2.875rem;
    padding: 0;
    border-radius: 0.875rem;
    background: ${({ theme }) => theme.colors.surface};
    color: ${({ theme }) => theme.colors.ink2};
    border: 1px solid ${({ theme }) => theme.colors.inputBorder};
    cursor: pointer;
    transition: all ${({ theme }) => theme.motion.fast} ease;

    svg {
        width: 1rem; height: 1rem;
    }

    &:hover {
        background: ${({ theme }) => theme.colors.accent};
        border-color: ${({ theme }) => theme.colors.accent};
        color: ${({ theme }) => theme.colors.ink};
    }

    &:active {
        transform: translateY(1px);
    }

    @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
        width: 2.5rem;
        height: 2.5rem;
    }
`;
